import { CheckCircle2, AlertCircle, XCircle, Info } from "lucide-react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Sidebar from "./components/Sidebar";
import Home from "./pages/Home";
import DiagnosticsOverview from "./pages/diagnostics/Overview";
import WebsiteDiagnosticTool from "./pages/diagnostics/WebsiteTool";
import MarketingTool from "./pages/diagnostics/MarketingTool";
import BusinessAssessmentTool from "./pages/diagnostics/BusinessAssessmentTool";
import TalentDiagnosticsTool from "./pages/diagnostics/TalentDiagnosticsTool";
import MarketingLeadsPage from "./pages/diagnostics/MarketingLeadsPage";
import BusinessLeadsPage from "./pages/diagnostics/BusinessLeadsPage";
import Login from "./pages/Login";
import AuthGuard from "./components/AuthGuard";
import AdminRoute from "./components/AdminRoute";
import { useAuth } from "./context/AuthContext";
import { Slide, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PublicProposalPage from "./components/PublicProposalPage";
import PublicDiagnosticReportPage from "./pages/PublicDiagnosticReportPage";
import Settings from "./pages/Settings";
import PaymentSuccessPage from "./pages/payments/PaymentSuccessPage";
import PaymentCancelledPage from "./pages/payments/PaymentCancelledPage";

import PublicJobBoardPage from "./pages/job-board/PublicJobBoardPage";
import PublicJobDetailsPage from "./pages/job-board/PublicJobDetailsPage";
import ApplyForm from "./pages/job-board/ApplyForm";
import JobBoardRedirect from "./pages/job-board/JobBoardRedirect";
import JobBoardModule from "./pages/job-board/JobBoardModule";

// Billing Pages
import InvoicesModule from "./pages/billing/InvoicesModule";
import ProposalModule from "./pages/billing/ProposalModule";

// Booking & Scheduling Module
import BookingModule from "./pages/booking/BookingModule";
import PublicBookingPage from "./pages/PublicBookingPage";
import PublicNewsletterPage from "./pages/PublicNewsletterPage";
import LeadModule from "./pages/lead-capture/LeadModule";
import UserManagementModule from "./pages/user-management/UserManagementModule";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import PasswordExpiredPage from "./pages/auth/PasswordExpiredPage";
import SetupAccountPage from "./pages/auth/SetupAccountPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

import PublicForm from './pages/PublicForm';
import RequireAddOn from "./components/RequireAddOn";
import { PlatformAddOn } from "./types/userManagement";
import DiagnosticLeadsPage from "./pages/diagnostics/DiagnosticLeadsPage";
import GoogleLoginCallback from "./pages/auth/GoogleLoginCallback";
import RecruitmentDiagnosticsTool from "./pages/diagnostics/RecruitmentDiagnosticsTool";

const queryClient = new QueryClient();

function App() {
  const { token, claims, login } = useAuth();
  const isAuthenticated = !!token;

  function stopImpersonation() {
    const adminToken =
      localStorage.getItem('adminToken');

    const adminUser =
      localStorage.getItem('adminUser');

    const adminSubscription =
      localStorage.getItem('adminSubscription');

    if (!adminToken || !adminUser) {
      toast.error(
        'Original administrator session not found.'
      );

      return;
    }

    login(
      adminToken,
      JSON.parse(adminUser),
      adminSubscription
        ? JSON.parse(adminSubscription)
        : null
    );

    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSubscription');

    toast.success(
      'Returned to administrator account.'
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen bg-white">
        {isAuthenticated && <Sidebar />}
        <main className={`w-full flex flex-col ${isAuthenticated ? "flex-1" : "items-center justify-center"}`}>
          {
            claims?.impersonated === 'true' && (
              <div className="flex items-center justify-between border-b border-amber-300 bg-amber-100 px-4 py-3 text-sm text-amber-900">
                <div>
                  You are currently impersonating a user account.
                </div>

                <button
                  onClick={stopImpersonation}
                  className="rounded-md border border-amber-500 px-3 py-1 font-medium hover:bg-amber-200"
                >
                  Stop Impersonation
                </button>
              </div>
            )
          }
          <Routes>
          <Route
            path="/"
            element={
              <AuthGuard>
                <Home />
              </AuthGuard>
            }
          />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
          />
          <Route path="/login/google" element={<GoogleLoginCallback />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/setup" element={<SetupAccountPage />} />
          <Route path="/setup-password" element={<Navigate to="/setup" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/password-expired" element={<PasswordExpiredPage />} />
          {/* <Route 
            path="/register" 
            element={isAuthenticated ? <Navigate to="/" replace /> : <Register />} 
          /> */}
          <Route
            path="/diagnostics/overview"
            element={
              <AdminRoute>
                <DiagnosticsOverview />
              </AdminRoute>
            }
          />
          <Route
            path="/diagnostics/website-tool"
            element={
              // <AuthGuard>
                <WebsiteDiagnosticTool />
              // </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/marketing-tool"
            element={
              // <AuthGuard>
                <MarketingTool />
              // </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/business-assessment"
            element={
              // <AuthGuard>
                <BusinessAssessmentTool />
              // </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/talent-diagnostics"
            element={
              // <AuthGuard>
                <TalentDiagnosticsTool />
              // </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/recruitment-diagnostics"
            element={
              // <AuthGuard>
                <RecruitmentDiagnosticsTool />
              // </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/leads"
            element={
              <AdminRoute>
                <DiagnosticLeadsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/diagnostics/marketing-leads"
            element={
              <AuthGuard>
                <MarketingLeadsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/business-leads"
            element={
              <AuthGuard>
                <BusinessLeadsPage />
              </AuthGuard>
            }
          />
          
          {/* Job Board Module */}
          <Route path="/jobs/:slug/:jobId" element={<PublicJobDetailsPage />} />
          <Route path="/jobs/:slug" element={<PublicJobBoardPage />} />
          <Route path="/job-board/apply/:jobId" element={<ApplyForm />} />

          {/* Job Board Module */}
          <Route
            element={
              <RequireAddOn
                addOn={PlatformAddOn.JobBoard}
                showAccessDenied
              />
            }
          >
            <Route
              path="/admin/job-board/jobs"
              element={
                <Navigate
                  to="/job-board/jobs"
                  replace
                />
              }
            />

            <Route
              path="/admin/job-board/jobs/create"
              element={
                <Navigate
                  to="/job-board/jobs/create"
                  replace
                />
              }
            />

            <Route
              path="/admin/job-board/jobs/:id/edit"
              element={
                <JobBoardRedirect target="/job-board/jobs/:id/edit" />
              }
            />

            <Route
              path="/admin/job-board/jobs/:id/applicants"
              element={
                <JobBoardRedirect target="/job-board/jobs/:id/applicants" />
              }
            />

            <Route
              path="/admin/job-board/jobs/:id/applicants/:applicantId"
              element={
                <JobBoardRedirect target="/job-board/jobs/:id/applicants/:applicantId" />
              }
            />

            <Route
              path="/admin/job-board/jobs/:id"
              element={
                <JobBoardRedirect target="/job-board/jobs/:id" />
              }
            />

            <Route
              path="/admin/job-board/settings"
              element={
                <Navigate
                  to="/job-board/settings"
                  replace
                />
              }
            />

            <Route
              path="/job-board/*"
              element={
                <AuthGuard>
                  <JobBoardModule />
                </AuthGuard>
              }
            />
          </Route>
          {/* <Route path="/admin/job-board/jobs" element={<Navigate to="/job-board/jobs" replace />} />
          <Route path="/admin/job-board/jobs/create" element={<Navigate to="/job-board/jobs/create" replace />} />
          <Route path="/admin/job-board/jobs/:id/edit" element={<JobBoardRedirect target="/job-board/jobs/:id/edit" />} />
          <Route path="/admin/job-board/jobs/:id/applicants" element={<JobBoardRedirect target="/job-board/jobs/:id/applicants" />} />
          <Route path="/admin/job-board/jobs/:id/applicants/:applicantId" element={<JobBoardRedirect target="/job-board/jobs/:id/applicants/:applicantId" />} />
          <Route path="/admin/job-board/jobs/:id" element={<JobBoardRedirect target="/job-board/jobs/:id" />} />
          <Route path="/admin/job-board/settings" element={<Navigate to="/job-board/settings" replace />} />
          <Route
            path="/job-board/*"
            element={
              <AuthGuard>
                <JobBoardModule />
              </AuthGuard>
            }
          /> */}

          {/* Billing & Proposals Module */}
          <Route
            element={
              <RequireAddOn
                addOn={PlatformAddOn.BillingAndProposals}
                showAccessDenied
              />
            }
          >
            <Route
              path="/invoices"
              element={
                <AuthGuard>
                  <InvoicesModule />
                </AuthGuard>
              }
            />

            <Route
              path="/proposals/*"
              element={
                <AuthGuard>
                  <ProposalModule />
                </AuthGuard>
              }
            />
          </Route>

          <Route path="/proposal/:token" element={<PublicProposalPage />} />

          <Route path="/report/:token" element={<PublicDiagnosticReportPage />} />

          {/* Booking & Scheduling Module */}
          <Route
            element={
              <RequireAddOn
                addOn={PlatformAddOn.BookingAndScheduling}
                showAccessDenied
              />
            }
          >
            <Route
              path="/booking/*"
              element={
                <AuthGuard>
                  <BookingModule />
                </AuthGuard>
              }
            />
          </Route>
          <Route path="/book/:bookingSlug" element={<PublicBookingPage />} />
          <Route path="/newsletter/:slug" element={<PublicNewsletterPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
          
          {/* Forms Module */}
          <Route
            element={
              <RequireAddOn
                addOn={PlatformAddOn.Forms}
                showAccessDenied
              />
            }
          >
            <Route
              path="/forms/*"
              element={
                <AuthGuard>
                  <LeadModule />
                </AuthGuard>
              }
            />
          </Route>

          <Route
            path="/user-management"
            element={
              <AdminRoute>
                <UserManagementModule />
              </AdminRoute>
            }
          />

          {/* Settings Module */}
          <Route
            path="/settings/*"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
          <Route
            path="/:company/:slug/embed"
            element={<PublicForm embed />}
          />
          <Route path="/:company/:slug" element={<PublicForm />} />
        </Routes>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
        limit={3}
        toastClassName="!rounded-2xl"
        icon={({ type }) => {
          const iconClass =
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl';

          switch (type) {
            case 'success':
              return (
                <div
                  className={`${iconClass} bg-emerald-50 text-emerald-600`}
                >
                  <CheckCircle2 size={20} />
                </div>
              );

            case 'error':
              return (
                <div
                  className={`${iconClass} bg-red-50 text-red-600`}
                >
                  <XCircle size={20} />
                </div>
              );

            case 'warning':
              return (
                <div
                  className={`${iconClass} bg-amber-50 text-amber-600`}
                >
                  <AlertCircle size={20} />
                </div>
              );

            case 'info':
              return (
                <div
                  className={`${iconClass} bg-strivre-lightburgundy text-strivre-burgundy`}
                >
                  <Info size={20} />
                </div>
              );

            default:
              return null;
          }
        }}
      />
        </main>
      </div>
    </QueryClientProvider>
  );
}

export default App;
